import { useEffect, useRef } from 'react'

import { throttle } from 'lodash-es'

interface VisibilityConfig {
  /** 相交比例阈值，0-1，默认 0 表示任何可见即触发 */
  threshold?: number | number[]
  /** 根元素，默认 null（视口） */
  root?: Element | null
  /** 是否重复上报，默认 false */
  repeated?: boolean
  /** 节流间隔（ms），默认 300，设置为 0 可禁用 */
  throttleWait?: number
}

/**
 * 元素进入可视区时触发上报
 * - 可控制是否重复上报
 * - 支持节流，避免慢速滚动时重复触发
 *
 * @param target 元素id
 * @param onVisible 元素进入可视区时触发的回调，在回调内自行处理上报逻辑
 * @param config 配置项
 */
export const useVisibilityReport = (target: string, onVisible: () => void, config?: VisibilityConfig) => {
  const { threshold = 0.3, root = null, repeated = true, throttleWait = 300 } = config ?? {}

  const hasReported = useRef(false)
  const onVisibleRef = useRef(onVisible)

  // 保持 onVisible 引用最新
  useEffect(() => {
    onVisibleRef.current = onVisible
  }, [onVisible])

  useEffect(() => {
    if (!target) return

    const element = document.getElementById(target)

    const throttledCallback = throttle(
      () => {
        if (!repeated && hasReported.current) return

        if (!repeated) {
          hasReported.current = true
        }

        onVisibleRef.current()
      },
      throttleWait,
      { leading: true, trailing: false },
    )

    const ioCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          throttledCallback()
        }
      })
    }

    const observer = new IntersectionObserver(ioCallback, { root, threshold })

    if (element) {
      observer.observe(element)
    }

    const mutationObserver = new MutationObserver(() => {
      const el = document.getElementById(target)
      if (el) {
        observer.observe(el)
        mutationObserver.disconnect()
      }
    })

    mutationObserver.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      mutationObserver.disconnect()
      throttledCallback.cancel()
    }
  }, [target, threshold, root, repeated, throttleWait])
}
